/**
 * Mechanics with per-tick state that move things: pulls (Coiling Vortex), tethers (Death Rattle),
 * telegraphed charges (Burrowing Charge, Hunting Pounce) and staggered waves (Regurgitate).
 */
import type {
  ChargeMechanic,
  ChargeState,
  EffectCtx,
  PullMechanic,
  SimContext,
  TetherMechanic,
  Unit,
  WaveMechanic,
  WorldShape,
} from '../../contracts';
import { TICK_MS } from '../../contracts';
import {
  angleTo,
  arenaOf,
  dist,
  endTelegraph,
  hostilesOf,
  immuneToDisplacement,
  mstate,
  pickTargets,
  polar,
  pushTelegraph,
  pushZone,
  resolveKeyScaled,
  runEffects,
  unitOf,
} from './state';

/** Default charge dash speed (yards/sec). */
export const CHARGE_SPEED = 45;
/** Default pull reach (yards). */
export const PULL_RADIUS = 60;

// ---- pull ------------------------------------------------------------------------

export function applyPull(ctx: SimContext, spec: PullMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const castId = (spec.whileCasting ?? true) && src.cast?.abilityId === ectx.abilityId ? ectx.abilityId : null;
  mstate(ctx.world).pulls.push({
    id: ctx.newId('mp'),
    sourceId: src.id,
    abilityId: ectx.abilityId,
    center: spec.center === 'point' ? { ...(ectx.point ?? src.pos) } : null,
    endsAt: ctx.now + spec.durationMs,
    speed: spec.speed,
    radius: spec.radius ?? PULL_RADIUS,
    minDist: spec.minDist ?? 2,
    castAbilityId: castId,
  });
}

export function updatePulls(ctx: SimContext): void {
  const st = ctx.world.mechanics;
  if (!st || st.pulls.length === 0) return;
  const step = TICK_MS / 1000;
  st.pulls = st.pulls.filter((p) => {
    const src = unitOf(ctx, p.sourceId);
    if (!src?.alive || ctx.now >= p.endsAt) return false;
    if (p.castAbilityId && src.cast?.abilityId !== p.castAbilityId) return false;
    const c = p.center ?? src.pos;
    for (const u of hostilesOf(ctx, src, undefined)) {
      const d = dist(u.pos, c);
      if (d > p.radius || u.move?.mode === 'forced' || immuneToDisplacement(ctx, u)) continue;
      const move = p.speed > 0 ? Math.min(p.speed * step, Math.max(0, d - p.minDist)) : p.speed * step;
      if (move === 0 || d < 1e-6) continue;
      const nx = u.pos.x + ((c.x - u.pos.x) / d) * move;
      const ny = u.pos.y + ((c.y - u.pos.y) / d) * move;
      const to = { x: nx, y: ny };
      if (ctx.sys.spatial.isWalkable(ctx.world, to)) u.pos = to;
    }
    return true;
  });
}

// ---- tether ----------------------------------------------------------------------

export function applyTether(ctx: SimContext, spec: TetherMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const st = mstate(ctx.world);
  for (const u of pickTargets(ctx, src, spec.rule ?? 'all', 99, ectx.targetId)) {
    if (st.tethers.some((t) => t.unitId === u.id && t.sourceId === src.id)) continue;
    if (spec.whileTethered) ctx.sys.combat.applyAura(ctx, u.id, spec.whileTethered.id, src.id, ectx.abilityId, { durationMs: spec.durationMs });
    st.tethers.push({
      id: ctx.newId('mt'),
      sourceId: src.id,
      unitId: u.id,
      abilityId: ectx.abilityId,
      breakAt: spec.breakAt,
      startedAt: ctx.now,
      endsAt: ctx.now + spec.durationMs,
      failEffects: spec.failEffects ?? [],
      breakEffects: spec.breakEffects ?? [],
      auraId: spec.whileTethered?.id ?? null,
    });
  }
}

export function updateTethers(ctx: SimContext): void {
  const st = ctx.world.mechanics;
  if (!st || st.tethers.length === 0) return;
  const keep = [];
  for (const t of st.tethers) {
    const src = unitOf(ctx, t.sourceId);
    const u = unitOf(ctx, t.unitId);
    const drop = (): void => {
      if (t.auraId && u) ctx.sys.combat.removeAura(ctx, u.id, { auraId: t.auraId, casterId: t.sourceId }, 'cancelled');
    };
    if (!src?.alive || !u?.alive) {
      drop();
      continue;
    }
    const ectx: EffectCtx = { sourceId: src.id, abilityId: t.abilityId, targetId: u.id, point: { x: u.pos.x, y: u.pos.y }, depth: 1 };
    if (dist(src.pos, u.pos) > t.breakAt) {
      drop();
      runEffects(ctx, t.breakEffects, ectx);
      continue;
    }
    if (ctx.now >= t.endsAt) {
      drop();
      runEffects(ctx, t.failEffects, ectx);
      continue;
    }
    keep.push(t);
  }
  st.tethers = keep;
}

// ---- charge ----------------------------------------------------------------------

function chargeArea(src: Unit, target: Unit | undefined, c: Pick<ChargeState, 'length' | 'overshoot' | 'width'>, fallbackFacing: number): WorldShape {
  const facing = target ? angleTo(src.pos, target.pos) : fallbackFacing;
  const len = c.length ?? Math.max(4, (target ? dist(src.pos, target.pos) : 20) + c.overshoot);
  return { shape: { type: 'line', length: len, width: c.width }, origin: { x: src.pos.x, y: src.pos.y }, facing };
}

export function applyCharge(ctx: SimContext, spec: ChargeMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src?.alive) return;
  const target = pickTargets(ctx, src, spec.rule ?? 'primary', 1, ectx.targetId)[0];
  const base = { length: spec.length ?? null, overshoot: spec.overshoot ?? 4, width: spec.width };
  const area = chargeArea(src, target, base, src.facing);
  src.facing = area.facing;
  const tg = pushTelegraph(ctx, src.id, ectx.abilityId, area, ctx.now + spec.delayMs, { intent: spec.intent ?? 'avoid', mechanic: 'charge' });
  mstate(ctx.world).charges.push({
    id: tg.id,
    sourceId: src.id,
    abilityId: ectx.abilityId,
    targetId: target?.id ?? null,
    resolvesAt: tg.resolvesAt,
    aimUntil: ctx.now + (spec.aimFollowMs ?? 0),
    ...base,
    speed: spec.speed ?? CHARGE_SPEED,
    effects: spec.effects,
    trail: spec.trail ?? null,
    hold: { x: src.pos.x, y: src.pos.y },
  });
}

export function updateCharges(ctx: SimContext): void {
  const st = ctx.world.mechanics;
  if (!st || st.charges.length === 0) return;
  const keep = [];
  for (const c of st.charges) {
    const src = unitOf(ctx, c.sourceId);
    const tg = ctx.world.telegraphs.find((t) => t.id === c.id);
    if (!src?.alive || !tg) {
      if (tg) endTelegraph(ctx, c.id, []);
      continue;
    }
    // Wind-up: the caster holds still on its mark and faces the line.
    if (src.move?.mode !== 'forced') src.move = null;
    if (ctx.now < c.aimUntil) {
      const t = unitOf(ctx, c.targetId);
      if (t?.alive) tg.area = chargeArea(src, t, c, tg.area.facing);
    }
    src.facing = tg.area.facing;
    if (ctx.now < c.resolvesAt) {
      keep.push(c);
      continue;
    }
    const area = tg.area;
    const hits = ctx.sys.spatial.unitsInShape(ctx.world, area, { affects: 'enemies', relativeTo: src.id });
    for (const id of hits) {
      const u = ctx.world.units[id]!;
      runEffects(ctx, c.effects, { sourceId: src.id, abilityId: c.abilityId, targetId: id, point: { x: u.pos.x, y: u.pos.y }, depth: 1 });
    }
    const len = area.shape.type === 'line' ? area.shape.length : 0;
    if (c.trail) {
      const mid = polar(area.origin, area.facing, len / 2);
      pushZone(ctx, src.id, c.abilityId, c.trail, { shape: { type: 'rect', w: c.width, h: len }, origin: mid, facing: area.facing }, 'avoid');
    }
    let endPt = polar(area.origin, area.facing, Math.max(0, len - src.radius));
    // Never dash out of the boss arena (that would evade-reset the encounter).
    const arena = arenaOf(ctx, src);
    if (arena) {
      const m = src.radius + 1;
      endPt = { x: Math.min(arena.x + arena.w - m, Math.max(arena.x + m, endPt.x)), y: Math.min(arena.y + arena.h - m, Math.max(arena.y + m, endPt.y)) };
    }
    const end = ctx.sys.spatial.clampToWalkable(ctx.world, endPt);
    if (c.speed <= 0) {
      src.pos = end;
      src.move = null;
    } else {
      const d = dist(src.pos, end);
      src.move = { mode: 'forced', to: end, speed: c.speed, until: ctx.now + Math.max(TICK_MS, Math.round((d / c.speed) * 1000)) };
    }
    endTelegraph(ctx, c.id, hits);
  }
  st.charges = keep;
}

// ---- waves -----------------------------------------------------------------------

export function applyWave(ctx: SimContext, spec: WaveMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const target = unitOf(ctx, ectx.targetId);
  const anchor = spec.anchor ?? 'caster';
  const origin =
    anchor === 'target' && target ? { ...target.pos } : anchor === 'point' && ectx.point ? { ...ectx.point } : { x: src.pos.x, y: src.pos.y };
  const aim = target && target.id !== src.id ? angleTo(src.pos, target.pos) : ectx.point ? angleTo(src.pos, ectx.point) : src.facing;
  const bands = Math.max(1, Math.round(resolveKeyScaled(ctx, spec.bands, 1)));
  const start = spec.startAt ?? 0;
  for (let i = 0; i < bands; i++) {
    const inner = start + i * spec.bandWidth;
    let area: WorldShape;
    if (spec.form === 'ring') {
      area =
        inner <= 0
          ? { shape: { type: 'circle', radius: spec.bandWidth }, origin, facing: aim }
          : { shape: { type: 'ring', inner, outer: inner + spec.bandWidth }, origin, facing: aim };
    } else {
      area = { shape: { type: 'rect', w: spec.width ?? 8, h: spec.bandWidth }, origin: polar(origin, aim, inner + spec.bandWidth / 2), facing: aim };
    }
    pushTelegraph(ctx, src.id, ectx.abilityId, area, ctx.now + spec.delayMs + i * spec.intervalMs, {
      effects: spec.effects,
      intent: spec.intent ?? 'avoid',
      mechanic: 'wave',
    });
  }
}
